/**
 * Client-side helper for communicating with the stream Service Worker.
 *
 * Encapsulates SW registration, stream lifecycle, and automatic buffer
 * replay on page visibility change so that SearchBar.js stays clean.
 */

let swRegistration = null;

/**
 * Register the stream service worker. Call once at app startup.
 */
export async function registerStreamWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    swRegistration = await navigator.serviceWorker.register('/stream-sw.js');
    await navigator.serviceWorker.ready;
    return swRegistration;
  } catch (err) {
    console.warn('Stream SW registration failed:', err);
    return null;
  }
}

/**
 * Returns true if the SW is active, controlling this page, AND we're on a
 * mobile device where tab backgrounding suspends JS execution.
 * On desktop, direct fetch is more reliable — no SW overhead needed.
 */
export function isServiceWorkerStreamingSupported() {
  if (!(navigator.serviceWorker && navigator.serviceWorker.controller)) return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Start a proxied stream through the Service Worker.
 *
 * @param {Object} opts
 * @param {string} opts.url        - Full fetch URL
 * @param {Object} opts.body       - Request body (will be JSON-stringified)
 * @param {Object} opts.headers    - Request headers
 * @param {Function} opts.onEvent  - Called with { eventType, data } for each SSE event
 * @param {Function} opts.onDone   - Called when stream completes
 * @param {Function} opts.onError  - Called with error message string
 *
 * @returns {{ streamId: string, abort: Function }}
 */
export function createProxiedStream({ url, body, headers, onEvent, onDone, onError }) {
  const streamId = generateStreamId();
  const channel = new MessageChannel();
  let lastReceivedIndex = -1;
  let finished = false;

  // Listen for messages from SW on this channel
  channel.port1.onmessage = (event) => {
    const msg = event.data;

    switch (msg.type) {
      case 'SSE_CHUNK':
        handleChunk(msg.chunk);
        break;

      case 'STREAM_DONE':
        finished = true;
        cleanup();
        onDone();
        break;

      case 'STREAM_ERROR':
        finished = true;
        cleanup();
        onError(msg.error);
        break;

      case 'BUFFERED_CHUNKS':
        // Replay missed chunks from buffer request
        if (msg.chunks) {
          msg.chunks.forEach(handleChunk);
        }
        if (msg.status === 'done' && !finished) {
          finished = true;
          cleanup();
          onDone();
        } else if ((msg.status === 'error' || msg.status === 'not_found') && !finished) {
          finished = true;
          cleanup();
          onError(msg.status === 'not_found'
            ? 'Connection lost while app was in background. Please try again.'
            : 'Stream failed while backgrounded');
        }
        break;

      default:
        break;
    }
  };

  function handleChunk(chunk) {
    if (chunk.index <= lastReceivedIndex) return; // dedupe
    lastReceivedIndex = chunk.index;
    try {
      const data = JSON.parse(chunk.data);
      onEvent({ eventType: chunk.eventType, data });
    } catch (e) {
      console.error('Failed to parse SW chunk data:', chunk.data, e);
    }
  }

  // Handle page visibility change — request buffered chunks on resume
  function onVisibilityChange() {
    if (document.visibilityState === 'visible' && !finished && navigator.serviceWorker.controller) {
      const bufferChannel = new MessageChannel();
      bufferChannel.port1.onmessage = channel.port1.onmessage; // reuse same handler
      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_BUFFER', streamId, lastReceivedIndex },
        [bufferChannel.port2]
      );
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange);

  function cleanup() {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    // Tell SW it can free the buffer
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEANUP_STREAM', streamId });
    }
  }

  // Send START_STREAM to the SW
  navigator.serviceWorker.controller.postMessage(
    {
      type: 'START_STREAM',
      streamId,
      url,
      body,
      headers: headers || { 'Content-Type': 'application/json' },
    },
    [channel.port2]
  );

  return {
    streamId,
    abort: () => {
      if (!finished) {
        finished = true;
        cleanup();
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'ABORT_STREAM', streamId });
        }
        onError('Request aborted');
      }
    },
  };
}

function generateStreamId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'sw-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
}
